export * from "./url";
export * from "./chart";
export * from "./error";
export * from "./digio";
export * from "./zip";

// Environment Detection Utilities
export const ENVIRONMENTS = {
  DEVELOPMENT: "development",
  STAGING: "nonprod",
  PRODUCTION: "production",
} as const;

export type Environment = (typeof ENVIRONMENTS)[keyof typeof ENVIRONMENTS];

export function getCurrentEnvironment(): Environment {
  const viteEnv = import.meta.env.VITE_DEPLOY_ENV;
  if (viteEnv && Object.values(ENVIRONMENTS).includes(viteEnv as Environment)) {
    return viteEnv as Environment;
  }

  // Fallback to Vite's built-in mode detection
  if (import.meta.env.PROD) {
    return ENVIRONMENTS.PRODUCTION;
  }

  if (import.meta.env.MODE === "staging") {
    return ENVIRONMENTS.STAGING;
  }

  return ENVIRONMENTS.DEVELOPMENT;
}

/**
 * Check if the current environment is development
 */
export function isDev(): boolean {
  return getCurrentEnvironment() === ENVIRONMENTS.DEVELOPMENT;
}

/**
 * Check if the current environment is staging
 */
export function isStaging(): boolean {
  return getCurrentEnvironment() === ENVIRONMENTS.STAGING;
}

/**
 * Check if the current environment is production
 */
export function isProduction(): boolean {
  return getCurrentEnvironment() === ENVIRONMENTS.PRODUCTION;
}

/**
 * Check if the current environment is NOT production (dev or staging)
 */
export function isNonProduction(): boolean {
  return !isProduction();
}

/**
 * Get environment-specific configuration
 */
export function getEnvironmentConfig() {
  const env = getCurrentEnvironment();

  const configs = {
    [ENVIRONMENTS.DEVELOPMENT]: {
      apiUrl: "https://non-prod.creso.in",
      debugMode: true,
      enableDevtools: true,
      logLevel: "debug",
      sentryEnabled: false,
    },
    [ENVIRONMENTS.STAGING]: {
      apiUrl: "https://non-prod.creso.in",
      debugMode: true,
      enableDevtools: false,
      logLevel: "info",
      sentryEnabled: true,
    },
    [ENVIRONMENTS.PRODUCTION]: {
      apiUrl: window.location.origin,
      debugMode: false,
      enableDevtools: false,
      logLevel: "error",
      sentryEnabled: true,
    },
  };

  return configs[env];
}

export function getOrigin() {
  const config = getEnvironmentConfig();
  return config.apiUrl;
}

export function getPdfDownloadUrl(path: string) {
  return `${getOrigin()}${path}`;
}
